import { Link } from 'react-router-dom';
import { FormEvent, useState } from 'react';

export default function Home() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4a26gm4v2qe61n4pgp0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                Points Made Simple
              </h1>
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="#home" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Home</a>
              <a href="#how-it-works" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">How It Works</a>
              <Link to="/cards" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Card Recommendations</Link>
              <Link to="/quiz" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Find the Best Card</Link>
              <a href="#about" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">About Me</a>
              <a href="#contact" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Contact</a>
            </div>
            <button className="md:hidden w-8 h-8 flex items-center justify-center">
              <i className="ri-menu-line text-2xl text-gray-700"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative bg-gradient-to-br from-teal-50 to-blue-50 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://static.readdy.ai/image/eb1e8ba596db94317d89b53c543659f0/680e61001ba05405c0cac057a8917e4f.jpeg" 
            alt="Travel destination" 
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Unlock Free Travel with Credit Card Points
            </h2>
            <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
              Learn how to turn everyday spending into dream vacations. No complicated jargon — just simple strategies that work.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#how-it-works" className="inline-block bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors text-center cursor-pointer whitespace-nowrap">
                Get Started
              </a>
              <Link to="/cards" className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold border-2 border-teal-600 hover:bg-teal-50 transition-colors text-center cursor-pointer whitespace-nowrap">
                View Card Recommendations
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <div className="text-5xl font-bold text-teal-600 mb-2">$50,000+</div>
              <div className="text-gray-600 text-lg">Worth of Free Travel</div>
            </div>
            <div className="p-6">
              <div className="text-5xl font-bold text-teal-600 mb-2">1.5M+</div>
              <div className="text-gray-600 text-lg">Points Redeemed</div>
            </div>
            <div className="p-6">
              <div className="text-5xl font-bold text-teal-600 mb-2">10+</div>
              <div className="text-gray-600 text-lg">Dream Destinations</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">How It Works</h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Three simple steps to start your journey to free travel
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-bank-card-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">1. Choose the Right Card</h4>
              <p className="text-gray-600 leading-relaxed">
                Start with beginner-friendly cards that offer generous sign-up bonuses and rewards on everyday purchases. I'll show you exactly which cards to get based on your goals.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shopping-cart-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">2. Earn Points Strategically</h4>
              <p className="text-gray-600 leading-relaxed">
                Use your cards for everyday spending you're already doing — groceries, gas, dining out. Watch your points stack up quickly with smart category bonuses.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-flight-takeoff-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">3. Redeem for Maximum Value</h4>
              <p className="text-gray-600 leading-relaxed">
                Learn the best redemption strategies to get 2-5x more value from your points. Book flights, hotels, and experiences that would normally cost thousands.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Find the Best Card CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-teal-600 to-blue-600 rounded-3xl p-12 md:p-16 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-questionnaire-line text-4xl text-white"></i>
              </div>
              <h3 className="text-4xl md:text-5xl font-bold mb-6">Not Sure Which Card to Choose?</h3>
              <p className="text-xl md:text-2xl text-teal-50 mb-8 leading-relaxed">
                Take my quick 5-question quiz and I'll recommend the perfect card based on your goals, budget, and travel preferences.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/quiz" className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap text-lg">
                  <i className="ri-play-circle-line mr-2"></i>
                  Find Your Best Card
                </Link>
                <Link to="/cards" className="inline-block bg-transparent text-white px-8 py-4 rounded-lg font-semibold border-2 border-white hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap text-lg">
                  View All Cards
                </Link>
              </div>
              <p className="text-teal-100 text-sm mt-4">Takes less than 2 minutes • Personalized recommendations</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Destinations */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Where Points Can Take You</h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real destinations I've visited using credit card points
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl shadow-lg mb-4 h-64">
                <img 
                  src="https://public.readdy.ai/ai/img_res/d9d5f5ddc60b950fa038b0a0a04c8c89.jpg" 
                  alt="Bora Bora" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h5 className="text-2xl font-bold">Bora Bora</h5>
                  <p className="text-sm">Overwater Bungalows</p>
                </div>
              </div>
            </div>

            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl shadow-lg mb-4 h-64">
                <img 
                  src="https://static.readdy.ai/image/eb1e8ba596db94317d89b53c543659f0/b64364f450e56af297763e899e8d15de.png" 
                  alt="Business Class" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h5 className="text-2xl font-bold">Hawaii</h5>
                  <p className="text-sm">Business Class Flights</p>
                </div>
              </div>
            </div>

            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl shadow-lg mb-4 h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=Beautiful%20beach%20resort%20in%20Bahamas%20with%20white%20sand%20beach%2C%20turquoise%20Caribbean%20water%2C%20palm%20trees%20swaying%2C%20luxury%20beachfront%20hotel%2C%20sunny%20tropical%20day%2C%20professional%20travel%20photography%2C%20vibrant%20colors%2C%20paradise%20island%20destination&width=600&height=400&seq=dest-bahamas&orientation=landscape" 
                  alt="Bahamas" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h5 className="text-2xl font-bold">Bahamas</h5>
                  <p className="text-sm">Beach Resort</p>
                </div>
              </div>
            </div>

            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl shadow-lg mb-4 h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=Stunning%20Maui%20Hawaii%20coastline%20with%20dramatic%20cliffs%2C%20lush%20green%20tropical%20vegetation%2C%20turquoise%20ocean%20waves%2C%20scenic%20coastal%20road%2C%20sunny%20day%20with%20blue%20sky%2C%20professional%20landscape%20photography%2C%20vibrant%20natural%20colors%2C%20paradise%20destination&width=600&height=400&seq=dest-maui&orientation=landscape" 
                  alt="Maui" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h5 className="text-2xl font-bold">Maui</h5>
                  <p className="text-sm">Hawaiian Paradise</p>
                </div>
              </div>
            </div>

            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl shadow-lg mb-4 h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=French%20Polynesia%20Tahiti%20tropical%20island%20with%20pristine%20beach%2C%20crystal%20clear%20lagoon%2C%20lush%20mountain%20peaks%2C%20palm%20trees%2C%20luxury%20resort%20setting%2C%20sunny%20paradise%20day%2C%20professional%20travel%20photography%2C%20vibrant%20turquoise%20and%20green%20colors&width=600&height=400&seq=dest-tahiti&orientation=landscape" 
                  alt="Tahiti" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h5 className="text-2xl font-bold">Tahiti</h5>
                  <p className="text-sm">French Polynesia</p>
                </div>
              </div>
            </div>

            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl shadow-lg mb-4 h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=Disney%20World%20Magic%20Kingdom%20castle%20at%20sunset%20with%20colorful%20sky%2C%20iconic%20Cinderella%20Castle%2C%20magical%20theme%20park%20atmosphere%2C%20families%20enjoying%20vacation%2C%20professional%20photography%2C%20vibrant%20colors%2C%20enchanting%20Disney%20experience&width=600&height=400&seq=dest-disney&orientation=landscape" 
                  alt="Disney World" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h5 className="text-2xl font-bold">Disney World</h5>
                  <p className="text-sm">Family Vacation</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://static.readdy.ai/image/eb1e8ba596db94317d89b53c543659f0/eabb9273cf6366179e3765915bc437a9.png" 
                alt="Travel couple" 
                className="rounded-2xl shadow-2xl w-full object-cover object-top"
              />
            </div>
            <div>
              <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">My Journey to Free Travel</h3>
              <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                <p>
                  Four years ago, I opened my first rewards credit card — and everything changed. Since then, my wife and I have turned everyday spending into over <strong>$50,000 worth of free travel</strong>, redeeming <strong>1.5M+ credit card points</strong> along the way.
                </p>
                <p>
                  What started as a simple curiosity has grown into a passion for traveling smarter, not spending more. Points have taken us across the world — from overwater bungalows in Bora Bora and lay-flat business class flights to Hawaii and Tahiti, to unforgettable trips to the Bahamas, Oahu, Kauai, Maui, Orange County, Disney World, Miami, and New York.
                </p>
                <p>
                  Now, I want to help others do the same — to show that with the right strategy, you can travel the world for free using the power of points and miles. Whether you're dreaming of your first big trip or looking to take your rewards game to the next level, I'll help you make it simple, step by step.
                </p>
              </div>
              <Link to="/cards" className="inline-block mt-8 bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                See My Card Recommendations
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Start Your Journey?</h3>
          <p className="text-xl text-teal-50 mb-8">
            Join me in discovering how to unlock free travel with credit card points. It's easier than you think.
          </p>
          <Link to="/cards" className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
            View Card Recommendations
          </Link>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Get In Touch</h3>
            <p className="text-xl text-gray-600">
              Have questions? I'd love to hear from you.
            </p>
          </div>
          
          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6 bg-gray-50 p-8 rounded-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
              <textarea 
                id="message" 
                name="message" 
                rows={6}
                required
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                placeholder="Tell me about your travel goals..."
              ></textarea>
              <p className="text-sm text-gray-500 mt-1">Maximum 500 characters</p>
            </div>
            
            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                <p className="font-semibold">Message sent successfully!</p>
                <p className="text-sm">I'll get back to you soon.</p>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                <p className="font-semibold">Something went wrong.</p>
                <p className="text-sm">Please try again later.</p>
              </div>
            )}
            
            <button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Pacifico", serif' }}>Points Made Simple</h4>
              <p className="text-gray-400">
                Helping beginners unlock free travel through credit card points and miles.
              </p>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Quick Links</h5>
              <ul className="space-y-2">
                <li><a href="#home" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</a></li>
                <li><a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors cursor-pointer">How It Works</a></li>
                <li><Link to="/cards" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Card Recommendations</Link></li>
                <li><a href="#about" className="text-gray-400 hover:text-white transition-colors cursor-pointer">About Me</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Connect</h5>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Points Made Simple. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
